import * as Sentry from '@sentry/react'

import { IGNORED_ERRORS } from './ignored-errors'
import { posthog } from './posthog'
import { getRecentApiErrors } from './recent-api-errors'
import { sendSupportReport } from './support-report'

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

// Derive a same-origin tunnel URL from the DSN so adblockers can't drop
// envelope POSTs by hostname or path. The prefix is intentionally random-
// looking (the literal "/sentry" was caught by EasyPrivacy filterlists);
// rotate the suffix here AND in infra/caddy/observability-tunnel.caddy
// together if it ever starts being blocked again. The path encodes ORG_ID
// + PROJECT_ID, which Caddy parses with a generic regex (no hardcoded IDs).
//   DSN:    https://KEY@o{ORG}.ingest.us.sentry.io/{PROJECT}
//   Tunnel: /relay-5hd7/o{ORG}/{PROJECT}/envelope/
//   Caddy:  → https://o{ORG}.ingest.us.sentry.io/api/{PROJECT}/envelope/
export function buildTunnelUrl(rawDsn: string): string | undefined {
    try {
        const url = new URL(rawDsn)
        const project = url.pathname.replace(/^\//, '')
        const org = url.host.match(/^o(\d+)\.ingest\./)?.[1]
        if (!org || !project) return undefined
        return `/relay-5hd7/o${org}/${project}/envelope/`
    } catch {
        return undefined
    }
}

// Resolve the Sentry environment from a dedicated build-time var so staging
// and production are distinguishable. `import.meta.env.MODE` is always
// "production" for any `vite build`, so it can't tell the two deploys apart;
// VITE_SENTRY_ENVIRONMENT is injected per deploy environment in CI.
// CD injects the release version as __APP_VERSION__; the same value names the
// source-map release in vite.config.ts. Local builds fall back to
// package.json's placeholder and must not claim a real release.
export function resolveSentryRelease(appVersion: string): string | undefined {
    if (!appVersion || appVersion === '0.0.0-dev') return undefined
    return appVersion
}

export function resolveSentryEnvironment(): string {
    return import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE
}

export function initSentry() {
    if (!dsn) return

    Sentry.init({
        dsn,
        environment: resolveSentryEnvironment(),
        release: resolveSentryRelease(__APP_VERSION__),
        tunnel: buildTunnelUrl(dsn),
        ignoreErrors: IGNORED_ERRORS,
        integrations: [
            Sentry.browserTracingIntegration(),
            // "Báo lỗi" feedback form. The trigger is the header icon button
            // (SupportReportTrigger) which calls attachTo on this integration,
            // so autoInject is off to suppress the library's floating button.
            // Submitted feedback is attached to a Sentry event scoped to the
            // current user (set in identifyUser), so admins see the user's
            // message + their session replay link (added by the
            // addEventProcessor below) in one issue.
            Sentry.feedbackIntegration({
                colorScheme: 'system',
                showBranding: false,
                autoInject: false,
                triggerLabel: 'Báo lỗi',
                triggerAriaLabel: 'Mở form báo lỗi',
                formTitle: 'Báo lỗi',
                submitButtonLabel: 'Gửi báo cáo',
                cancelButtonLabel: 'Hủy',
                confirmButtonLabel: 'Xác nhận',
                addScreenshotButtonLabel: 'Đính kèm ảnh chụp màn hình',
                removeScreenshotButtonLabel: 'Xóa ảnh chụp màn hình',
                nameLabel: 'Tên',
                namePlaceholder: 'Tên của bạn',
                emailLabel: 'Email',
                emailPlaceholder: 'email@example.com (không bắt buộc)',
                isEmailRequired: false,
                isNameRequired: false,
                messageLabel: 'Mô tả lỗi',
                messagePlaceholder:
                    'Bạn đang làm gì khi gặp lỗi? Nội dung lỗi hiện ra là gì?',
                successMessageText: 'Cảm ơn bạn đã báo lỗi!',
                // The feedback itself is stored by Sentry; this hook
                // additionally pings the dev Telegram chat with trace context
                // (recent req_ids, replay URL, app version) so support starts
                // without a round-trip to the user.
                onSubmitSuccess: (data: { message?: string }) => {
                    void sendSupportReport(data?.message ?? '')
                },
            }),
        ],
        tracesSampleRate: 0.2,
    })

    // Cross-link every Sentry event with the user's PostHog session so we can
    // jump from a Sentry issue → the exact replay timestamp without manual
    // ID copy-paste. Lives as an event processor (vs. the posthog-js Sentry
    // integration) to stay independent of the posthog-js Sentry-API surface.
    Sentry.addEventProcessor((event) => {
        const sessionId = posthog.get_session_id?.()
        if (sessionId) {
            event.tags = { ...event.tags, posthog_session_id: sessionId }
            const replayUrl = posthog.get_session_replay_url?.()
            if (replayUrl) {
                event.contexts = {
                    ...event.contexts,
                    posthog: { session_replay_url: replayUrl },
                }
            }
        }
        // Attach the latest failed-API req_ids so a feedback report (or any
        // error event) carries the exact trace IDs an admin needs to pull the
        // server-side call chain from Loki. Messages stay out — req_id,
        // status, code and path are enough for correlation.
        const recentErrors = getRecentApiErrors()
        if (recentErrors.length > 0) {
            event.contexts = {
                ...event.contexts,
                api_errors: {
                    recent: recentErrors.map((e) => ({
                        req_id: e.requestId,
                        status: e.status,
                        code: e.code,
                        path: e.path,
                        at: e.at,
                    })),
                },
            }
        }
        return event
    })
}
